// Local storage utilities for appointment management
import { 
  fetchAppointments, 
  createAppointment, 
  updateAppointmentStatus as apiUpdateStatus,
  deleteAppointment as apiDeleteAppointment,
  fetchNews,
  createNews as apiCreateNews,
  updateNews as apiUpdateNews,
  deleteNews as apiDeleteNews,
  fetchGallery,
  createGallery as apiCreateGallery,
  updateGallery as apiUpdateGallery,
  deleteGallery as apiDeleteGallery,
  uploadImage as apiUploadImage,
  ApiAppointment,
  ApiNewsItem,
  ApiGalleryItem,
  CreateAppointmentData,
  CreateNewsData,
  CreateGalleryData
} from './api';

export interface Appointment {
  id: string;
  fullName: string;
  phone: string;
  email: string;
  department: string;
  preferredDate: string;
  message: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  createdAt: string;
}

const convertApiAppointment = (apiApt: ApiAppointment): Appointment => ({
  id: apiApt.id,
  fullName: apiApt.full_name,
  phone: apiApt.phone,
  email: apiApt.email || '',
  department: apiApt.department,
  preferredDate: apiApt.preferred_date,
  message: apiApt.message || '',
  status: apiApt.status,
  createdAt: apiApt.created_at
});

export const getAppointments = async (): Promise<Appointment[]> => {
  try {
    console.log('📋 Loading appointments...');
    const apiAppointments = await fetchAppointments();
    console.log('📋 API appointments received:', apiAppointments.length);
    return apiAppointments.map(convertApiAppointment);
  } catch (error) {
    console.error('Error loading appointments:', (error as Error).message);
    // Fallback to localStorage if API fails
    const stored = localStorage.getItem('rabininsia_appointments');
    return stored ? JSON.parse(stored) : [];
  }
};

export const addAppointment = async (appointmentData: CreateAppointmentData): Promise<Appointment> => {
  console.log('📝 Adding appointment:', appointmentData);
  
  try {
    const apiAppointment = await createAppointment(appointmentData);
    console.log('✅ Appointment created via API:', apiAppointment);
    return convertApiAppointment(apiAppointment);
  } catch (error) {
    console.error('❌ Error creating appointment via API:', error);
    console.error('❌ Error details:', (error as Error).message);
    
    // Fallback to localStorage
    const newAppointment: Appointment = {
      id: Date.now().toString(),
      fullName: appointmentData.fullName,
      phone: appointmentData.phone,
      email: appointmentData.email || '',
      department: appointmentData.department,
      preferredDate: appointmentData.preferredDate,
      message: appointmentData.message || '',
      status: 'pending',
      createdAt: new Date().toISOString()
    };
    
    console.log('💾 Saving to localStorage as fallback:', newAppointment);
    const stored = localStorage.getItem('rabininsia_appointments');
    const appointments = stored ? JSON.parse(stored) : [];
    appointments.unshift(newAppointment);
    localStorage.setItem('rabininsia_appointments', JSON.stringify(appointments));
    
    // Show error to user but still return the appointment
    alert('Warning: Appointment saved locally. Please check your internet connection.');
    
    return newAppointment;
  }
};

export const updateAppointmentStatus = async (id: string, status: Appointment['status']): Promise<void> => {
  try {
    await apiUpdateStatus(id, status);
  } catch (error) {
    console.error('Error updating appointment status:', error);
    throw error;
  }
};

export const deleteAppointment = async (id: string): Promise<void> => {
  try {
    await apiDeleteAppointment(id);
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
};

// News management
export interface NewsItem {
  id: string;
  title: string;
  content: string;
  date: string;
  author: string;
  status: 'published' | 'draft';
  category: 'news' | 'announcement' | 'event';
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GalleryItem {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

const convertApiNews = (apiNews: ApiNewsItem): NewsItem => ({
  id: apiNews.id,
  title: apiNews.title,
  content: apiNews.content,
  date: apiNews.date,
  author: apiNews.author,
  status: apiNews.status,
  category: apiNews.category,
  imageUrl: apiNews.image_url,
  createdAt: apiNews.created_at,
  updatedAt: apiNews.updated_at
});

// Convert API gallery to local format
const convertApiGallery = (apiGallery: ApiGalleryItem): GalleryItem => ({
  id: apiGallery.id,
  title: apiGallery.title,
  description: apiGallery.description,
  imageUrl: apiGallery.image_url,
  category: apiGallery.category,
  createdAt: apiGallery.created_at,
  updatedAt: apiGallery.updated_at
});

export const getNews = async (): Promise<NewsItem[]> => {
  try {
    const apiNews = await fetchNews();
    return apiNews.map(convertApiNews);
  } catch (error) {
    console.error('Error loading news:', error);
    // Fallback to localStorage if API fails
    const stored = localStorage.getItem('rabininsia_news');
    return stored ? JSON.parse(stored) : [];
  }
};

export const addNews = async (newsData: CreateNewsData): Promise<NewsItem> => {
  try {
    const apiNews = await apiCreateNews(newsData);
    return convertApiNews(apiNews);
  } catch (error) {
    console.error('Error creating news:', error);
    // Fallback to localStorage
    const newNews: NewsItem = {
      id: Date.now().toString(),
      title: newsData.title,
      content: newsData.content,
      date: newsData.date,
      author: newsData.author,
      status: newsData.status,
      category: newsData.category,
      imageUrl: newsData.imageUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const stored = localStorage.getItem('rabininsia_news');
    const news = stored ? JSON.parse(stored) : [];
    news.unshift(newNews);
    localStorage.setItem('rabininsia_news', JSON.stringify(news));
    
    return newNews;
  }
};

export const updateNews = async (id: string, newsData: Partial<CreateNewsData>): Promise<void> => {
  try {
    await apiUpdateNews(id, newsData);
  } catch (error) {
    console.error('Error updating news:', error);
    throw error;
  }
};

export const deleteNews = async (id: string): Promise<void> => {
  try {
    await apiDeleteNews(id);
  } catch (error) {
    console.error('Error deleting news:', error);
    throw error;
  }
};

// Gallery management
export const getGallery = async (): Promise<GalleryItem[]> => {
  try {
    console.log('📡 Fetching gallery from API...');
    const apiGallery = await fetchGallery();
    console.log('📡 API gallery response:', apiGallery);
    return apiGallery.map(convertApiGallery);
  } catch (error) {
    console.error('Error loading gallery:', error);
    // Fallback to localStorage if API fails
    const stored = localStorage.getItem('rabininsia_gallery');
    console.log('💾 Fallback to localStorage:', stored ? 'found' : 'empty');
    return stored ? JSON.parse(stored) : [];
  }
};

export const addGallery = async (galleryData: CreateGalleryData): Promise<GalleryItem> => {
  try {
    const apiGallery = await apiCreateGallery(galleryData);
    return convertApiGallery(apiGallery);
  } catch (error) {
    console.error('Error creating gallery item:', error);
    // Fallback to localStorage
    const newGallery: GalleryItem = {
      id: Date.now().toString(),
      title: galleryData.title,
      description: galleryData.description,
      imageUrl: galleryData.imageUrl,
      category: galleryData.category,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const stored = localStorage.getItem('rabininsia_gallery');
    const gallery = stored ? JSON.parse(stored) : [];
    gallery.unshift(newGallery);
    localStorage.setItem('rabininsia_gallery', JSON.stringify(gallery));
    
    return newGallery;
  }
};

export const updateGallery = async (id: string, galleryData: Partial<CreateGalleryData>): Promise<void> => {
  try {
    await apiUpdateGallery(id, galleryData);
  } catch (error) {
    console.error('Error updating gallery:', error);
    throw error;
  }
};

export const deleteGallery = async (id: string): Promise<void> => {
  try {
    await apiDeleteGallery(id);
  } catch (error) {
    console.error('Error deleting gallery item:', error);
    throw error;
  }
};

export const uploadImage = async (file: File): Promise<{ imageUrl: string }> => {
  try {
    return await apiUploadImage(file);
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};